import React, {
  createContext,
  DependencyList,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import isShallowEqual from 'shallowequal'
import { createStoreWithAdditionalState } from './store'
import { IStateContext, State, Store } from './types'

export const StateContext = createContext<IStateContext | null>(null)

const useShallowEffect = (fn: () => void, deps: DependencyList) => {
  const ref = useRef<{ deps: DependencyList }>({
    deps: Array.from({ length: deps.length }),
  })

  useEffect(() => {
    if (
      !deps.every((value, index) =>
        isShallowEqual(value, ref.current.deps[index])
      )
    ) {
      fn()
      ref.current = { deps }
    }
  })
}

export const StateProvider: React.FC<{
  store?: Store
  additionalState?: State
  namespace?: string
  children?: React.ReactNode
}> = (props) => {
  const upstreamStateContext = useContext(StateContext)
  const rootStore =
    upstreamStateContext?.rootStore ??
    upstreamStateContext?.store ??
    props.store
  const upstreamStore = upstreamStateContext?.store ?? props.store

  if (!rootStore) {
    throw new Error('Cannot create StateProvider without a root store')
  }

  if (!upstreamStore) {
    throw new Error('Cannot create StateProvider without a store')
  }

  const additionalStateRef = useRef(props.additionalState)
  const storeRef = useRef(
    createStoreWithAdditionalState(
      rootStore,
      upstreamStore,
      additionalStateRef,
      props.namespace
    )
  )

  useShallowEffect(() => {
    additionalStateRef.current = props.additionalState
    storeRef.current.dispatch()
  }, [storeRef, props.additionalState])

  const stateContext = useMemo(
    () => ({
      rootStore,
      store: storeRef.current,
    }),
    [rootStore, storeRef]
  )

  return (
    <StateContext.Provider value={stateContext}>
      {props.children}
    </StateContext.Provider>
  )
}
